import { memo, ReactElement, useEffect, useState } from 'react';
import { DataGridPro, GridCellParams, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';
import { Paper, Tooltip } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DeliveryDTO, sortDirection } from '../../api/generated';
import { Paginated } from '../../api/common';
import { getDataGridLocaleText, localizedDateFormatter, skipUndefined } from '../../utils';
import { useTranslation } from 'react-i18next';

/** Replacement for the removed GridSortItem type from MUI X v8 */
interface GridSortItem {
  field: string;
  sort: 'asc' | 'desc' | null | undefined;
}

const columns: GridColDef[] = [
  {
    field: 'delivery_id',
    resizable: false,
    flex: 0.5,
    type: 'string',
  },
  {
    field: 'supplier',
    resizable: false,
    flex: 0.7,
    type: 'string',
  },
  {
    field: 'material_id',
    resizable: false,
    flex: 0.5,
    type: 'string',
  },
  {
    field: 'material',
    resizable: false,
    flex: 0.7,
    type: 'string',
  },
  {
    field: 'avv',
    resizable: false,
    flex: 0.5,
    type: 'string',
  },
  {
    field: 'date',
    resizable: false,
    flex: 0.8,
    type: 'dateTime',
    valueFormatter: localizedDateFormatter,
  },
  {
    field: 'has_truck_scan_img',
    resizable: false,
    flex: 0.3,
    type: 'boolean',
    filterable: true,
  },
  {
    field: 'netto_weight',
    resizable: false,
    flex: 0.5,
    type: 'number',
  },
  {
    field: 'numberplate',
    resizable: false,
    flex: 0.5,
    type: 'string',
    valueFormatter: (value: string | undefined) => skipUndefined(value, (v: string) => v.toUpperCase()),
  },
  {
    field: 'construction_site',
    resizable: false,
    flex: 1,
    type: 'string',
  },
];

export type DeliveriesTableData = Paginated<DeliveryDTO>;

export interface DeliverySorting extends GridSortItem {
  field: keyof DeliveryDTO;
  sort: sortDirection;
}

export interface DeliveriesTableProps {
  data: DeliveriesTableData | null; // null indicates loading state
  selectedId?: string | null;
  selectedEntries?: string[];
  onSelectEntry?: (selectedIds: string[]) => void;
  pageSize: number;
  page: number;
  onSelect?: (deliveryId: string | null) => void;
  onPageChange: (newPage: number) => void;
  onSortChange: (sorting: DeliverySorting | null) => void;
}

function _deliveryToRow(delivery: DeliveryDTO): object {
  return {
    ...delivery,
    date: new Date(delivery.date),
    has_truck_scan_img: !!delivery.pictureIds?.length,
  };
}

export const DeliveriesTable = memo(function ({
  data,
  onSelect,
  selectedId,
  selectedEntries = [],
  onSelectEntry,
  pageSize,
  page,
  onPageChange,
  onSortChange,
}: DeliveriesTableProps): ReactElement {
  const { t } = useTranslation();

  const [lastValidTotal, setLastValidTotal] = useState(0);

  useEffect(() => {
    if (data && Number.isInteger(data.total)) setLastValidTotal(data.total);
  }, [data]);

  function handleSortModelChange(model: GridSortModel): void {
    // NOTE: multi-sorting is disabled
    onSortChange(model ? (model[0] as DeliverySorting) : null);
  }

  function handleRowSelectionModelChange(model: GridRowSelectionModel): void {
    const ids = model.type === 'include' ? Array.from(model.ids) : [];
    if (onSelectEntry) {
      onSelectEntry(ids as string[]);
      return;
    }
    if (!ids.length) {
      onSelect?.(null);
      return;
    }
    // NOTE: multi-select is disabled for detail view navigation
    const selected = ids[0];
    if (typeof selected == 'string') {
      onSelect?.(selected);
    } else {
      throw new Error('expected string for selection');
    }
  }

  const translatedColumns = columns.map((col) => ({
    ...col,
    headerName: t(`delivery.${col.field}`),
    ...(col.field === 'has_truck_scan_img' && {
      renderCell: (params: GridCellParams) => {
        if (params.value)
          return (
            <Tooltip title={t('tooltip.imageAvailable')} arrow>
              <CheckCircleIcon />
            </Tooltip>
          );
        else
          return (
            <Tooltip title={t('tooltip.imageNotAvailable')} arrow>
              <ErrorOutlineIcon />
            </Tooltip>
          );
      },
    }),
  }));

  return (
    <Paper
      className='data-grid'
      elevation={8}
      sx={{
        borderRadius: '10px',
        display: 'flex',
        width: '100%',
        flex: 1,
        minHeight: 0,
      }}
    >
      <DataGridPro
        sx={{ borderRadius: '10px' }}
        localeText={getDataGridLocaleText()}
        columns={translatedColumns}
        rows={data?.data.map(_deliveryToRow) || []}
        loading={!data}
        disableColumnPinning
        disableColumnFilter
        disableColumnSelector
        disableColumnMenu
        // Enable checkboxes for multi-selection
        checkboxSelection={!!onSelectEntry}
        disableMultipleRowSelection={!onSelectEntry}
        disableRowSelectionOnClick={!!onSelectEntry}
        // Handle row clicks for detail view
        onRowClick={(params) => onSelect?.(params.id as string)}
        // pagination
        pagination
        paginationMode='server'
        pageSizeOptions={[pageSize]} // Having only one option removes the dropdown menu
        paginationModel={{ pageSize, page }}
        onPaginationModelChange={(model) => onPageChange(model.page)}
        rowCount={lastValidTotal}
        // sorting
        sortingMode='server'
        onSortModelChange={handleSortModelChange}
        sortingOrder={['asc', 'desc']}
        disableMultipleColumnsSorting
        filterMode='server'
        // selection
        onRowSelectionModelChange={handleRowSelectionModelChange}
        rowSelectionModel={
          onSelectEntry
            ? { type: 'include', ids: new Set(selectedEntries) }
            : selectedId
              ? { type: 'include', ids: new Set([selectedId]) }
              : { type: 'include', ids: new Set() }
        }
      />
    </Paper>
  );
});
